import { useCallback, useEffect, useRef } from 'react';

import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { isInternalErrorEntry } from '../forms-core/state/formfield-messages-selectors.js';
import { normalizeMessageTarget, withTargetOutlet } from './message-routing.js';
import {
  NativeValidityStateKeys,
  ValidationError,
  type ValidationState,
} from './types.js';

/**
 * Returns a wrapped `emitValidationState` that always merges current custom
 * error messages into every emitted state. Also fires when the set of custom
 * errors changes (added or removed), using `getBaseState` to determine the
 * underlying native/Strato errors at that point.
 * @internal
 */
export function useCustomMessageEmitter(
  emitValidationState: ((state: ValidationState) => void) | undefined,
  getBaseState?: () => ValidationState | Promise<ValidationState>,
): (state: ValidationState) => ValidationState {
  const { state } = useFormFieldMessagesContext();

  const customErrors = state.errors.filter(
    (entry) => !isInternalErrorEntry(entry),
  );

  // Stable primitive dep: detects add/remove/swap by ID and message text changes.
  const customErrorKey = customErrors
    .map(({ id, error }) => `${id}:${error.message}`)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .join(',');

  // Read via ref so callbacks always see current values without re-creating.
  const customErrorsRef = useRef(customErrors);
  customErrorsRef.current = customErrors;

  const formControlIdRef = useRef(state.formControlId);
  formControlIdRef.current = state.formControlId;

  const messageTargetRef = useRef(state.messageTarget);
  messageTargetRef.current = state.messageTarget;

  const isTouchedRef = useRef(state.isTouched);
  isTouchedRef.current = state.isTouched;

  const showErrorRef = useRef(state.showError);
  showErrorRef.current = state.showError;

  const getBaseStateRef = useRef(getBaseState);
  getBaseStateRef.current = getBaseState;

  // Wrapped emitter: merges current custom errors into every emitted state so
  // that onChange/onBlur calls never silently drop an active custom error.
  // Returns the merged state so callers (e.g. validateAsync) can return it
  // directly instead of the raw pre-merge state.
  const emitWithCustomErrors = useCallback(
    (validationState: ValidationState): ValidationState => {
      // native/customError is set via setCustomValidity and is already
      // represented by our explicit custom entries — exclude to avoid doubling.
      const baseErrors = validationState.errors.filter(
        (e) => !e.validityStateKey.equals(NativeValidityStateKeys.customError),
      );

      // Prefer the form control ID from native errors (already knows the right
      // input element). Fall back to the context-registered form-control id
      // when no native error carries one.
      const baseControlId =
        baseErrors.find((e) => e.formControlId !== undefined)?.formControlId ??
        formControlIdRef.current;

      // Only native/Strato messages are routable. Annotate them with the
      // resolved outlet so the runtime can surface them in the form outlet;
      // custom (consumer-authored) errors always place themselves at the field.
      const targetOutlet = normalizeMessageTarget(messageTargetRef.current);
      const routedBaseErrors =
        targetOutlet.length === 1 && targetOutlet[0] === 'field'
          ? baseErrors
          : baseErrors.map((e) => withTargetOutlet(e, targetOutlet));

      const merged: ValidationState = {
        ...validationState,
        errors: [
          ...routedBaseErrors,
          ...customErrorsRef.current.map(({ error }) =>
            error.formControlId !== undefined
              ? error
              : new ValidationError(error.message, {
                  source: 'custom',
                  formControlId: baseControlId,
                }),
          ),
        ],
      };

      emitValidationState?.(merged);
      return merged;
    },
    [emitValidationState],
  );

  const customErrorKeyRef = useRef(customErrorKey);
  customErrorKeyRef.current = customErrorKey;

  const scheduleEmit = useCallback(() => {
    let cancelled = false;
    const run = async () => {
      const baseState = (await getBaseStateRef.current?.()) ?? {
        status: 'done' as const,
        trigger: 'manual' as const,
        errors: [],
      };
      if (cancelled) {
        return;
      }
      emitWithCustomErrors(baseState);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [emitWithCustomErrors]);

  // Fires when the custom error set changes (add, remove, swap) so the consumer
  // always gets an up-to-date state without a value change being required.
  // Also fires while untouched if `showError` is already forced (e.g. a
  // `showImmediately` error), which bypasses the touched gate for rendering
  // and must bypass it here too so `onValidityChange` stays in sync.
  useEffect(() => {
    if (
      !emitValidationState ||
      (!isTouchedRef.current && !showErrorRef.current)
    ) {
      return;
    }
    return scheduleEmit();
  }, [customErrorKey, emitValidationState, scheduleEmit]);

  // Fires when isTouched flips to true while custom errors are already pending,
  // covering the case where errors were set before the field was ever blurred.
  useEffect(() => {
    if (
      !emitValidationState ||
      !isTouchedRef.current ||
      !customErrorKeyRef.current
    ) {
      return;
    }
    return scheduleEmit();
  }, [state.isTouched, emitValidationState, scheduleEmit]);

  return emitWithCustomErrors;
}
